// Prize Pool helpers for the partner portal: the offer form ⇄ API payload, the
// client-side mirror of the API rules (D8), and the "max discount" preview.
// Pure.

export const OFFER_TYPES = ['cart_coupon', 'gift_rain', 'wheel_spin'];

export const EMPTY_OFFER = Object.freeze({
  id: null,
  version: null,
  title: '',
  type: 'cart_coupon',
  discount_type: 'percent',
  discount_value: '',
  currency: 'HUF',
  min_cart_value: '',
  max_uses: '',
  valid_from: '',
  valid_until: '',
  monthly_cap_qty: '',
  combinable_with_brand_sales: false,
  product_ids: [],
});

const dateInput = (iso) => (iso ? String(iso).slice(0, 10) : '');
const str = (v) => (v == null ? '' : String(v));

export function formFromOffer(o) {
  return {
    id: o.id,
    version: o.version,
    title: o.title ?? '',
    type: o.type,
    discount_type: o.discount_type ?? '',
    discount_value: str(o.discount_value),
    currency: o.currency ?? 'HUF',
    min_cart_value: str(o.min_cart_value),
    max_uses: str(o.max_uses),
    valid_from: dateInput(o.valid_from),
    valid_until: dateInput(o.valid_until),
    monthly_cap_qty: str(o.monthly_cap_qty),
    combinable_with_brand_sales: Boolean(o.combinable_with_brand_sales),
    product_ids: o.product_ids ?? [],
  };
}

const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

// Whole days: valid_from at 00:00 UTC (before the 09:00 Budapest start in every
// season), valid_until at 23:59:59 UTC (after the Budapest midnight end in both
// CET and CEST), so a picked day always covers that day's challenge.
const dayStart = (d) => (d ? `${d}T00:00:00Z` : null);
const dayEnd = (d) => (d ? `${d}T23:59:59Z` : null);

/** What the API takes. A gift crate carries products and no discount. */
export function payloadFromForm(form) {
  const crate = form.type === 'gift_rain';
  const discount = crate ? null : form.discount_type || null;
  const fixed = discount === 'fixed';
  return {
    title: form.title.trim() || null,
    type: form.type,
    discount_type: discount,
    discount_value: discount ? numOrNull(form.discount_value) : null,
    currency: fixed ? (form.currency || '').toUpperCase() : null,
    min_cart_value: discount ? numOrNull(form.min_cart_value) : null,
    max_uses: numOrNull(form.max_uses),
    valid_from: dayStart(form.valid_from),
    valid_until: dayEnd(form.valid_until),
    monthly_cap_qty: numOrNull(form.monthly_cap_qty),
    combinable_with_brand_sales: discount ? Boolean(form.combinable_with_brand_sales) : false,
    product_ids: form.type === 'cart_coupon' ? null : (form.product_ids.length ? form.product_ids : null),
  };
}

const positiveInt = (v) => Number.isInteger(Number(v)) && Number(v) >= 1;

/** The first problem of the form, or null (mirrors the API's rules, D8). */
export function offerProblem(form) {
  const crate = form.type === 'gift_rain';
  if (crate || form.type === 'wheel_spin') {
    if (crate && form.product_ids.length === 0) return 'products';
    if (form.type === 'wheel_spin' && form.product_ids.length === 0 && !form.discount_type) return 'products';
  }
  if (!crate && (form.type === 'cart_coupon' || form.discount_type)) {
    if (!form.discount_type) return 'discount';
    const value = Number(form.discount_value);
    if (form.discount_value === '' || !(value > 0)) return 'value';
    if (form.discount_type === 'percent' && value > 100) return 'value';
    if (form.discount_type === 'fixed') {
      if (!/^[A-Za-z]{3}$/.test(form.currency || '')) return 'currency';
      if (!(Number(form.min_cart_value) > value)) return 'minCart';
      if (!positiveInt(form.max_uses)) return 'maxUses';
      if (!form.valid_until) return 'validUntil';
    }
  }
  if (form.monthly_cap_qty !== '' && !positiveInt(form.monthly_cap_qty)) return 'cap';
  if (form.max_uses !== '' && !positiveInt(form.max_uses)) return 'maxUses';
  if (form.valid_from && form.valid_until && form.valid_from > form.valid_until) return 'validity';
  return null;
}

const round1 = (n) => Math.round(n * 10) / 10;

/**
 * The D8 preview: the biggest discount a shopper can get from this coupon —
 * on its own, or stacked on the brand's biggest running automatic percent sale
 * when the coupon is combinable. Percent stacking applies to the already
 * reduced price: 1 − (1 − sale)(1 − coupon).
 */
export function maxDiscountPreview(form, brandSalePercent = 0) {
  if (form.type === 'gift_rain' || !form.discount_type) return null;
  const value = Number(form.discount_value);
  if (!(value > 0)) return null;
  const sale = Number(brandSalePercent) || 0;
  if (form.discount_type === 'percent') {
    const stacked = round1(100 * (1 - (1 - sale / 100) * (1 - value / 100)));
    return {
      kind: 'percent',
      coupon: value,
      combinable: Boolean(form.combinable_with_brand_sales),
      sale,
      max: form.combinable_with_brand_sales && sale > 0 ? stacked : value,
    };
  }
  const minCart = Number(form.min_cart_value);
  return {
    kind: 'fixed',
    coupon: value,
    currency: (form.currency || '').toUpperCase(),
    minCart: minCart > 0 ? minCart : null,
    maxShare: minCart > 0 ? round1((value / minCart) * 100) : null,
    combinable: Boolean(form.combinable_with_brand_sales),
    sale,
  };
}

/** Which fields an offer that holds reservations still lets you change (mirror of the API). */
export const EDITABLE_WHEN_RESERVED = ['title', 'monthly_cap_qty', 'max_uses', 'valid_until'];
